import { useState } from "react";
import { useContasDespesas, ContaDespesas, ContaDespesasFormData } from "@/features/contabil/hooks/useContasDespesas";
import { useGruposDespesas } from "@/features/contabil/hooks/useGruposDespesas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Ban } from "lucide-react";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

export default function ContasDespesaReceita() {
  const { contas, isLoading, createConta, updateConta, deleteConta } = useContasDespesas();
  const { grupos } = useGruposDespesas();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ContaDespesas | null>(null);
  const [inativarId, setInativarId] = useState<number | null>(null);
  const [formData, setFormData] = useState<ContaDespesasFormData>({
    descricao: "", referencia: "", grupoDespesasId: null, isDespesa: true, isInativo: false,
  });
  const [busca, setBusca] = useState("");

  const handleOpen = (item?: ContaDespesas) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        descricao: item.descricao || "", referencia: item.referencia || "",
        grupoDespesasId: item.grupoDespesasId, isDespesa: item.isDespesa, isInativo: item.isInativo,
      });
    } else {
      setEditingItem(null);
      setFormData({ descricao: "", referencia: "", grupoDespesasId: null, isDespesa: true, isInativo: false });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingItem) {
      await updateConta.mutateAsync({ id: editingItem.id, ...formData });
    } else {
      await createConta.mutateAsync(formData);
    }
    setDialogOpen(false);
  };

  const contasFiltradas = busca
    ? contas.filter(c =>
        c.descricao?.toLowerCase().includes(busca.toLowerCase()) ||
        (c.referencia ?? "").toLowerCase().includes(busca.toLowerCase()))
    : contas;

  const paginacao = usePaginacaoLocal(contasFiltradas);

  if (isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Contas de Despesa/Receita</h1>
          <p className="text-muted-foreground">Gerencie as contas de despesa e receita</p>
        </div>
        <Button onClick={() => handleOpen()}>
          <Plus className="h-4 w-4 mr-2" />Nova Conta
        </Button>
      </div>

      {/* Busca */}
      <div className="max-w-sm">
        <Input
          placeholder="Buscar por descrição ou referência..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Descrição</TableHead>
              <TableHead>Referência</TableHead>
              <TableHead>Grupo</TableHead>
              <TableHead className="text-center">Tipo</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.descricao || "-"}</TableCell>
                <TableCell>{item.referencia || "-"}</TableCell>
                <TableCell>{grupos.find(g => g.id === item.grupoDespesasId)?.descricao || "-"}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={item.isDespesa ? "destructive" : "default"}>{item.isDespesa ? "Despesa" : "Receita"}</Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={!item.isInativo ? "success" : "secondary"}>{!item.isInativo ? "Ativo" : "Inativo"}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleOpen(item)}><Pencil className="h-4 w-4" /></Button>
                    {!item.isInativo && (
                      <Button variant="ghost" size="icon" onClick={() => setInativarId(item.id)}><Ban className="h-4 w-4" /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {contasFiltradas.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">{busca ? "Nenhuma conta encontrada para esta busca." : "Nenhuma conta cadastrada"}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Editar Conta" : "Nova Conta"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Descrição *</Label>
                <Input value={formData.descricao} onChange={(e) => setFormData({ ...formData, descricao: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Referência</Label>
                <Input value={formData.referencia} onChange={(e) => setFormData({ ...formData, referencia: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Grupo</Label>
              <Select
                value={formData.grupoDespesasId?.toString() || ""}
                onValueChange={(v) => setFormData({ ...formData, grupoDespesasId: v ? parseInt(v) : null })}
              >
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {grupos.filter(g => !g.isInativo).map((g) => (
                    <SelectItem key={g.id} value={g.id.toString()}>{g.descricao}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Switch id="despesa" checked={formData.isDespesa ?? true} onCheckedChange={(v) => setFormData({ ...formData, isDespesa: v })} />
                <Label htmlFor="despesa">É Despesa</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="ativo" checked={!formData.isInativo} onCheckedChange={(v) => setFormData({ ...formData, isInativo: !v })} />
                <Label htmlFor="ativo">Ativo</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!inativarId} onOpenChange={() => setInativarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar conta</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja inativar esta conta?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (inativarId) { deleteConta.mutate(inativarId); setInativarId(null); } }}>Inativar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
